import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import type { OnboardResult } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { generatePassword } from '../../lib/password';
import {
  EMPTY_ADMIN_STEP,
  EMPTY_ORG_STEP,
  adminStepProblems,
  effectiveSlug,
  onboardingBody,
  onboardingSummary,
  orgStepProblems,
  slugify,
  type AdminStep,
  type OrgStep,
  type WelcomeChoice,
} from '../../lib/onboarding';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, Field, Input } from '../../components/ui';

const STEPS = [
  { key: 'org', title: 'Organization' },
  { key: 'admin', title: 'Administrator' },
  { key: 'review', title: 'Review' },
];

/**
 * New organization — the tenant and the person who runs it, in one pass.
 *
 * Operator, 2026-09-17: "lets create a wizard for creating a new organization
 * and it's first admin". Three steps in the platform's stepped look: the
 * organization (name, slug, who to contact), its first administrator (name,
 * email — their username — phone, and a password the wizard can generate),
 * and a Review that asks the API for a dry run: the sign-in address the
 * admin will be given, every contact as it will be stored, whether a welcome
 * mail can go, and every reason the create would be refused. Create is not
 * offered while the dry run lists an issue.
 *
 * The password is shown at Review, once, with a copy button — the dialog
 * closes on Create (the platform's modal rule) and the platform never shows
 * a password again. The welcome mail carries the sign-in address and the
 * username; the password rides in it only when the operator ticks that.
 */
export function NewOrganizationWizard({ onClose }: { onClose: () => void }) {
  const tasks = useTasks();
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState<OrgStep>(EMPTY_ORG_STEP);
  const [admin, setAdmin] = useState<AdminStep>(EMPTY_ADMIN_STEP);
  const [welcome, setWelcome] = useState<WelcomeChoice>({ email: false, includePassword: false });
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  /** Problems are shown once Next has been pressed, not while the operator is still typing. */
  const [tried, setTried] = useState(false);

  const plan = useMutation({
    mutationFn: (body: ReturnType<typeof onboardingBody>) => api.post<OnboardResult>('/orgs/onboard', body),
  });

  const problems = step === 0 ? orgStepProblems(org) : step === 1 ? adminStepProblems(admin) : [];
  const issues = plan.data?.plan.issues ?? [];
  const review = plan.data?.plan ?? null;
  const canCreate = step === STEPS.length - 1 && plan.isSuccess && issues.length === 0;

  const next = () => {
    if (problems.length > 0) {
      setTried(true);
      return;
    }
    setTried(false);
    if (step === 1) plan.mutate(onboardingBody(org, admin, welcome, true));
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    setTried(false);
    setStep((s) => Math.max(0, s - 1));
  };

  /**
   * Create hands the write to the background tray and closes, like every
   * other wizard. The tray's sentence names the organization by number and
   * the administrator by email, and says what became of the welcome mail.
   */
  const create = () => {
    const body = onboardingBody(org, admin, welcome, false);
    onClose();
    tasks.start<OnboardResult>({
      label: `New organization — ${body.org.name}`,
      href: '/organizations',
      run: () => api.post<OnboardResult>('/orgs/onboard', body),
      done: onboardingSummary,
      invalidate: [['orgs'], ['session'], ['users']],
    });
  };

  const copyPassword = async () => {
    await navigator.clipboard.writeText(admin.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <WizardDialog
      title="New organization"
      subtitle="A tenant on this platform, and the administrator who runs it."
      maxWidth="2xl"
      steps={STEPS}
      currentStep={step}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && <Button onClick={back}>Back</Button>}
            {step < STEPS.length - 1 ? (
              <Button variant="primary" onClick={next}>
                Next
              </Button>
            ) : (
              <Button variant="primary" onClick={create} disabled={!canCreate} loading={plan.isPending}>
                Create
              </Button>
            )}
          </div>
        </div>
      }
    >
      {step === 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <Input
              value={org.name}
              onChange={(e) => setOrg({ ...org, name: e.target.value })}
              placeholder="MConnect FTTH"
              autoFocus
            />
          </Field>
          <Field
            label="Slug"
            hint="The GenieACS org:<slug> tag and the tenant's subdomain. Lowercase letters, digits and dashes."
          >
            <Input
              value={org.slug}
              onChange={(e) => setOrg({ ...org, slug: e.target.value })}
              placeholder={slugify(org.name) || 'mconnect-ftth'}
            />
          </Field>
          <Field label="Contact name" hint="Optional — who the platform talks to about this tenant.">
            <Input value={org.contactName} onChange={(e) => setOrg({ ...org, contactName: e.target.value })} />
          </Field>
          <Field label="Contact email" hint="Optional.">
            <Input
              type="email"
              value={org.contactEmail}
              onChange={(e) => setOrg({ ...org, contactEmail: e.target.value })}
            />
          </Field>
          <Field label="Contact phone" hint="Optional. International format, e.g. +254712345678.">
            <Input value={org.contactPhone} onChange={(e) => setOrg({ ...org, contactPhone: e.target.value })} />
          </Field>
          <Problems show={tried} problems={problems} />
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <Input value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} autoFocus />
          </Field>
          <Field label="Email" hint="Their username on this platform.">
            <Input
              type="email"
              value={admin.email}
              onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
            />
          </Field>
          <Field label="Phone" hint="Optional. International format, e.g. +254712345678.">
            <Input value={admin.phone} onChange={(e) => setAdmin({ ...admin, phone: e.target.value })} />
          </Field>
          <Field
            label="Password"
            hint="At least 10 characters. Generate makes one without look-alike characters; you hand it over at Review."
          >
            <div className="flex gap-2">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={admin.password}
                onChange={(e) => setAdmin({ ...admin, password: e.target.value })}
                autoComplete="new-password"
                className="flex-1"
              />
              <Button
                type="button"
                icon={showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              />
              <Button
                type="button"
                icon={<RefreshCw className="size-4" />}
                onClick={() => {
                  setAdmin({ ...admin, password: generatePassword() });
                  setShowPassword(true);
                }}
              >
                Generate
              </Button>
            </div>
          </Field>
          <Problems show={tried} problems={problems} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          {plan.isPending && <p className="text-sm text-muted">Checking the slug, the email and the sign-in address…</p>}
          {plan.isError && (
            <div className="rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm text-danger">
              {plan.error instanceof Error ? plan.error.message : 'The dry run failed.'}
            </div>
          )}
          {review && (
            <>
              {issues.length > 0 && (
                <div className="rounded-lg border border-danger/40 bg-danger/5 p-3 text-sm">
                  <p className="font-medium text-danger">Not created yet — go back and fix:</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-danger">
                    {issues.map((i) => (
                      <li key={`${i.field}:${i.message}`}>{i.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Organization</h3>
                <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
                  <Row label="Name">{review.org.name}</Row>
                  <Row label="Slug">
                    <span className="font-mono text-xs">{effectiveSlug(org)}</span>
                  </Row>
                  <Row label="Sign-in address">
                    <span className="font-mono text-xs">{review.org.signInUrl}</span>
                    {review.org.subdomain === null && (
                      <span className="ml-2 text-xs text-muted">
                        (the platform's address — no base domain is set, so the tenant gets no subdomain yet)
                      </span>
                    )}
                  </Row>
                  <Row label="Contact">
                    {[review.org.contactName, review.org.contactEmail, review.org.contactPhone].filter(Boolean).join(' · ') ||
                      '—'}
                  </Row>
                </dl>
              </section>

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Administrator</h3>
                <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
                  <Row label="Name">{review.admin.name}</Row>
                  <Row label="Username">{review.admin.email}</Row>
                  <Row label="Phone">{review.admin.phone ?? '—'}</Row>
                  <Row label="Password">
                    <span className="inline-flex items-center gap-2">
                      <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs">
                        {showPassword ? admin.password : '•'.repeat(Math.min(admin.password.length, 16))}
                      </code>
                      <Button
                        size="sm"
                        type="button"
                        icon={showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      />
                      <Button
                        size="sm"
                        type="button"
                        icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                        onClick={copyPassword}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </span>
                    <p className="mt-1 text-xs text-muted">
                      Shown here once. Copy it now — the dialog closes on Create and the platform never shows a
                      password again.
                    </p>
                  </Row>
                </dl>
              </section>

              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Welcome email</h3>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={welcome.email}
                    disabled={!review.welcome.canSend}
                    onChange={(e) => setWelcome({ ...welcome, email: e.target.checked })}
                  />
                  <span>
                    Email {review.admin.email} their sign-in address and username.
                    {!review.welcome.canSend && review.welcome.reason && (
                      <span className="block text-xs text-muted">{review.welcome.reason}</span>
                    )}
                  </span>
                </label>
                {welcome.email && (
                  <label className="flex items-start gap-2 pl-6 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={welcome.includePassword}
                      onChange={(e) => setWelcome({ ...welcome, includePassword: e.target.checked })}
                    />
                    <span>
                      Include the password in the email.
                      <span className="block text-xs text-muted">
                        Plain text in a mailbox. Leave this off to hand it over yourself.
                      </span>
                    </span>
                  </label>
                )}
              </section>
            </>
          )}
        </div>
      )}
    </WizardDialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function Problems({ show, problems }: { show: boolean; problems: string[] }) {
  if (!show || problems.length === 0) return null;
  return (
    <ul className="list-disc space-y-0.5 pl-5 text-sm text-danger sm:col-span-2">
      {problems.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}
